import StyledInput from '@/components/styled/StyledInput';
import MarginRateStyledInput from '@/components/styled/MarginRateStyledInput';
import { YesNoStyledRadioGroup } from '@/components/styled/StyledRadioGroup';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { Control, FieldPath, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import AgencyOverlayPlaceholderField from './AgencyOverlayPlaceholderField';
import clsx from 'clsx';
import { parseMarginRateNumber } from '@/utils/common/parseMarginRateInput';

interface EstimateInternationalFlightsCardProps {
  control: Control<EstimateFormData>;
  isViewMode?: boolean;
  taxesFieldName?: FieldPath<EstimateFormData>;
  priceFieldName?: FieldPath<EstimateFormData>;
  margedFieldName?: FieldPath<EstimateFormData>;
  flightsMarginRateFieldName?: FieldPath<EstimateFormData>;
  title?: string;
  /** Disabled inputs/radios (TO control or agency preview of TO values). */
  readOnly?: boolean;
  /** When readOnly, render even if values are empty. */
  showWhenEmpty?: boolean;
  /** TO preview of agency-owned tariffs: layout only, values masked. */
  hideValues?: boolean;
  muted?: boolean;
}

const EstimateInternationalFlightsCard = ({
  control,
  isViewMode = false,
  taxesFieldName = 'internationalFlightsNonMarginedTaxes',
  priceFieldName = 'internationalFlightsPrice',
  margedFieldName = 'marginRateExistsForInternationalFlights',
  flightsMarginRateFieldName = 'internationalFlightsMarginRate',
  title,
  readOnly = false,
  showWhenEmpty = false,
  hideValues = false,
  muted = false,
}: EstimateInternationalFlightsCardProps) => {
  const { t } = useTranslation();

  const nonMarginedTaxes = useWatch({ control, name: taxesFieldName });
  const internationalFlightsPrice = useWatch({
    control,
    name: priceFieldName,
  });
  const marginRateExistsForInternationalFlights = useWatch({
    control,
    name: margedFieldName,
  });
  const internationalFlightsMarginRate = useWatch({
    control,
    name: flightsMarginRateFieldName,
  });

  const taxesValue =
    typeof nonMarginedTaxes === 'number'
      ? nonMarginedTaxes
      : Number(nonMarginedTaxes) || 0;
  const priceValue =
    typeof internationalFlightsPrice === 'number'
      ? internationalFlightsPrice
      : Number(internationalFlightsPrice) || 0;
  const marginRateValue =
    parseMarginRateNumber(internationalFlightsMarginRate) ?? 0;

  const hasFlightsData =
    taxesValue > 0 ||
    priceValue > 0 ||
    marginRateValue > 0 ||
    marginRateExistsForInternationalFlights === 'true';

  const showFlightsMarginRate =
    hideValues || marginRateExistsForInternationalFlights === 'true';

  if (!hideValues && !readOnly && isViewMode) return null;
  if (!hideValues && readOnly && !showWhenEmpty && !hasFlightsData) return null;

  const titleClass = muted ? 'text-gray-500' : 'text-black';
  const labelClass = muted ? 'text-gray-500' : 'text-gray-700';

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1 className={clsx('text-xl font-medium', titleClass)}>
            {title ?? t('estimates.details.internationalFlights.title')}
          </h1>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hideValues ? (
          <>
            <AgencyOverlayPlaceholderField
              label={t(
                'estimates.details.internationalFlights.nonMarginedTaxesField',
              )}
              muted={muted}
            />
            <div className="grid gap-4 md:grid-cols-3 md:gap-x-4">
              <AgencyOverlayPlaceholderField
                label={t(
                  'estimates.details.internationalFlights.htAmountField',
                )}
                muted={muted}
              />
              <AgencyOverlayPlaceholderField
                label={t('estimates.details.internationalFlights.margedField')}
                muted={muted}
              />
              <AgencyOverlayPlaceholderField
                label={t(
                  'estimates.details.internationalFlights.flightsMarginRateField',
                )}
                muted={muted}
              />
            </div>
          </>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label className={labelClass}>
                {t(
                  'estimates.details.internationalFlights.nonMarginedTaxesField',
                )}
              </Label>
              <StyledInput
                control={control}
                name={taxesFieldName}
                type="number"
                hideNumberFieldArrows
                disabled={readOnly}
                className="w-full gap-0"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-3 md:gap-x-4">
              <div className="space-y-1.5">
                <Label className={labelClass}>
                  {t('estimates.details.internationalFlights.htAmountField')}
                </Label>
                <StyledInput
                  control={control}
                  name={priceFieldName}
                  type="number"
                  hideNumberFieldArrows
                  disabled={readOnly}
                  className="w-full gap-0"
                />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>
                  {t('estimates.details.internationalFlights.margedField')}
                </Label>
                <div className="flex h-10 items-center">
                  <YesNoStyledRadioGroup
                    control={control}
                    name={margedFieldName}
                    inline
                    disabled={readOnly}
                    className="shrink-0"
                  />
                </div>
              </div>
              {showFlightsMarginRate && (
                <div className="space-y-1.5">
                  <Label className={labelClass}>
                    {t(
                      'estimates.details.internationalFlights.flightsMarginRateField',
                    )}
                  </Label>
                  <MarginRateStyledInput
                    control={control}
                    name={flightsMarginRateFieldName}
                    disabled={readOnly}
                    className="w-full gap-0"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EstimateInternationalFlightsCard;
